import React, { useState, useRef, useEffect } from 'react';
import { 
  ChatBubbleLeftRightIcon, 
  XMarkIcon, 
  PaperAirplaneIcon,
  PhoneIcon,
  MinusIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const ChatBot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      id: 1,
      text: "Hi! I'm your TeethKart assistant 🦷 How can I help you find the perfect dental products today?",
      sender: 'bot',
      timestamp: new Date()
    }
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([
    "Show me dental chairs",
    "I need filling materials",
    "What's on sale today?",
    "Help me choose products"
  ]);

  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (message = inputMessage) => {
    if (!message.trim()) return;

    const userMessage = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      // Demo chat responses based on message content
      const getDemoResponse = (message) => {
        const msg = message.toLowerCase();
        
        if (msg.includes('chair') || msg.includes('equipment')) {
          return {
            text: "We have a great selection of dental chairs! 🪑\n\n• Premium Electric Dental Chairs\n• Basic Manual Chairs\n• Pediatric Dental Chairs\n• Portable Dental Units\n\nWould you like to see our equipment catalog?",
            suggestions: ["View Chairs", "Equipment Catalog", "Compare Models", "Contact Sales"]
          };
        } else if (msg.includes('filling') || msg.includes('composite')) {
          return {
            text: "Looking for filling materials? We stock the best! 🦷\n\n• Composite Resins\n• Glass Ionomer Cements\n• Amalgam Materials\n• Bonding Agents\n\nAll from trusted brands like 3M, Dentsply, and more.",
            suggestions: ["View Materials", "Brand Catalog", "Bulk Orders", "Technical Support"]
          };
        } else if (msg.includes('sale') || msg.includes('offer') || msg.includes('discount')) {
          return {
            text: "Great timing! We have amazing offers today! 🎉\n\n• 20% off on all instruments\n• Free shipping on orders above ₹5,000\n• Bundle deals on starter kits\n• EMI options available\n\nCheck out our deals page!",
            suggestions: ["View Deals", "Instrument Offers", "Starter Kits", "EMI Options"]
          };
        } else if (msg.includes('help') || msg.includes('support')) {
          return {
            text: "I'm here to help you find the perfect dental products! 💪\n\n• Product recommendations\n• Technical specifications\n• Bulk order assistance\n• Delivery information\n\nWhat specific help do you need?",
            suggestions: ["Product Help", "Technical Info", "Bulk Orders", "Delivery Info"]
          };
        } else {
          return {
            text: "Thanks for your message! I'm here to help with all your dental supply needs. 🦷\n\n• Browse 2,000+ products\n• Get expert recommendations\n• Check latest offers\n• Quick order assistance\n\nHow can I assist you today?",
            suggestions: ["Browse Products", "Recommendations", "Latest Offers", "Quick Order"]
          };
        }
      };
      
      const responseData = getDemoResponse(message);
      
      const botMessage = {
        id: Date.now() + 1,
        text: responseData.text,
        sender: 'bot',
        timestamp: new Date(),
        suggestions: responseData.suggestions
      };

      setMessages(prev => [...prev, botMessage]);
      setSuggestions(responseData.suggestions);
      
      // Occasionally show WhatsApp prompt for demo
      if (Math.random() > 0.7) {
        setTimeout(() => {
          const whatsappMessage = {
            id: Date.now() + 2,
            text: "Would you like to continue this conversation on WhatsApp for faster support?",
            sender: 'bot',
            timestamp: new Date(),
            isWhatsAppPrompt: true
          };
          setMessages(prev => [...prev, whatsappMessage]);
        }, 1500);
      }
    } catch (error) {
      console.error('Chat error:', error);
      
      // Fallback response
      const demoResponse = {
        id: Date.now() + 1,
        text: "I'm here to help! 🦷 While our AI assistant is loading, here are some quick options:\n\n• Browse our 2,000+ dental products\n• Check out today's special offers\n• Need immediate help? Contact us on WhatsApp!\n\nWhat would you like to explore?",
        sender: 'bot',
        timestamp: new Date(),
        suggestions: ["Browse Products", "Today's Offers", "Contact WhatsApp", "View Categories"]
      };
      
      setMessages(prev => [...prev, demoResponse]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleWhatsAppRedirect = async () => {
    // Direct WhatsApp link without API call
    const whatsappURL = 'https://wa.me/918999250629?text=Hello! I need assistance with dental products from TeethKart.';
    window.open(whatsappURL, '_blank');
    toast.success('Opening WhatsApp...');
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!isOpen) {
    return (
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(true)}
          className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 animate-pulse-slow"
        >
          <ChatBubbleLeftRightIcon className="h-6 w-6" />
          <span className="absolute -top-2 -left-2 bg-red-500 text-white text-xs rounded-full px-2 py-1 animate-bounce">
            Chat
          </span>
        </button>
      </div>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
      isMinimized ? 'h-12' : 'h-96 sm:h-[500px]'
    }`}>
      <div className="bg-white rounded-lg shadow-2xl border border-gray-200 w-80 sm:w-96 h-full flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 text-white p-4 rounded-t-lg flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-white/20 p-2 rounded-full">
              <span className="text-lg">🦷</span>
            </div>
            <div>
              <h3 className="font-semibold">TeethKart Assistant</h3>
              <p className="text-xs text-white/80">
                Online now
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsMinimized(!isMinimized)}
              className="text-white/80 hover:text-white p-1"
            >
              <MinusIcon className="h-4 w-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="text-white/80 hover:text-white p-1"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
        </div>

        {!isMinimized && (
          <>
            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
              {messages.map((message) => (
                <div key={message.id} className="animate-fade-in-up">
                  <div className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                    <div className={`max-w-xs px-4 py-2 rounded-lg ${
                      message.sender === 'user'
                        ? 'bg-primary-600 text-white'
                        : 'bg-white text-gray-800 shadow-sm border'
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{message.text}</p>
                      <p className={`text-xs mt-1 ${
                        message.sender === 'user' ? 'text-white/70' : 'text-gray-500'
                      }`}>
                        {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                      </p>
                    </div>
                  </div>

                  {/* WhatsApp prompt */}
                  {message.isWhatsAppPrompt && (
                    <div className="flex justify-center mt-2">
                      <button
                        onClick={handleWhatsAppRedirect}
                        className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded-lg flex items-center space-x-2 text-sm transition-colors"
                      >
                        <PhoneIcon className="h-4 w-4" />
                        <span>Continue on WhatsApp</span>
                      </button>
                    </div>
                  )}

                  {/* Suggestions */}
                  {message.suggestions && message.sender === 'bot' && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {message.suggestions.map((suggestion, index) => (
                        <button
                          key={index}
                          onClick={() => handleSuggestionClick(suggestion)}
                          className="bg-primary-100 hover:bg-primary-200 text-primary-700 px-3 py-1 rounded-full text-xs transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}

              {isLoading && (
                <div className="flex justify-start">
                  <div className="bg-white text-gray-800 shadow-sm border max-w-xs px-4 py-2 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <div className="loading-spinner"></div>
                      <span className="text-sm">Typing...</span>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef} />
            </div>

            {/* Quick suggestions */}
            {suggestions.length > 0 && (
              <div className="px-4 py-2 border-t bg-gray-50">
                <div className="flex flex-wrap gap-2">
                  {suggestions.slice(0, 3).map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSuggestionClick(suggestion)}
                      className="bg-white hover:bg-gray-100 text-gray-700 px-3 py-1 rounded-full text-xs border transition-colors"
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Input */}
            <div className="p-4 border-t bg-white rounded-b-lg">
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  value={inputMessage}
                  onChange={(e) => setInputMessage(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask about dental products..."
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                  disabled={isLoading}
                />
                <button
                  onClick={() => sendMessage()}
                  disabled={isLoading || !inputMessage.trim()}
                  className="bg-primary-600 hover:bg-primary-700 disabled:bg-gray-400 text-white p-2 rounded-lg transition-colors"
                >
                  <PaperAirplaneIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ChatBot;
